<template>
    <div class="max-w-screen-md">
        <progress-bar
            :class="['!h-2', isLoading ? '' : 'invisible']"
            mode="indeterminate"
        />
        <div v-if="cartData === null && !isLoading">
            <app-message theme="danger">
                {{ $t('purchaseContract.invalidParticipantsPricing') }}
            </app-message>
        </div>
        <div
            v-else-if="cartData !== undefined && formattedCartItems.length > 0"
        >
            <data-table :value="formattedCartItems">
                <column header="Item">
                    <template #body="{ data }">
                        <div>
                            {{ data.description }}
                        </div>
                        <div class="text-sm italic">
                            {{ data.note }}
                        </div>
                    </template>
                </column>

                <column header="Price">
                    <template #body="slotProps">
                        <div class="flex items-start">
                            {{
                                slotProps.data.quantity
                            }}&nbsp;&nbsp;@&nbsp;&nbsp;
                            <div class="inline-block">
                                {{ formatCurrency(slotProps.data.unitPrice) }}
                                <template
                                    v-if="
                                        isNotEqual(
                                            slotProps.data.unitPrice,
                                            slotProps.data.originalUnitPrice
                                        )
                                    "
                                >
                                    <br />
                                    <span class="line-through">{{
                                        formatCurrency(
                                            slotProps.data.originalUnitPrice
                                        )
                                    }}</span>
                                </template>
                            </div>
                        </div>
                    </template>
                </column>

                <column header="Total">
                    <template #body="slotProps">
                        <div class="text-right">
                            {{ formatCurrency(slotProps.data.extendedTotal) }}
                        </div>
                    </template>
                </column>
            </data-table>

            <div class="my-6 flex w-full justify-end">
                <ul class="m-0 w-1/2 space-y-3 p-0">
                    <li class="flex justify-between">
                        <span class="text-slate-700">{{
                            $t('checkout.subtotal')
                        }}</span>
                        <span class="text-slate-700 text-lg font-medium">{{
                            formatCurrency(cartData.subtotal)
                        }}</span>
                    </li>

                    <li
                        v-if="inclusiveSalesTax.isNotZero()"
                        class="flex justify-between"
                    >
                        <span class="text-slate-700">{{
                            $t('checkout.inclusiveTax')
                        }}</span>
                        <span class="text-slate-700 text-lg font-medium">{{
                            inclusiveSalesTax.formatCurrency()
                        }}</span>
                    </li>

                    <li class="flex justify-between">
                        <span class="text-slate-700">{{
                            $t('checkout.tax')
                        }}</span>
                        <span class="text-slate-700 text-lg font-medium">{{
                            formatCurrency(cartData.exclusiveTaxTotal)
                        }}</span>
                    </li>

                    <li class="flex justify-between border-t pt-3">
                        <span class="text-slate-700 font-medium">{{
                            $t('checkout.total')
                        }}</span>
                        <span class="text-slate-700 text-lg font-semibold">{{
                            formatCurrency(cartData.total)
                        }}</span>
                    </li>

                    <li
                        v-if="
                            paymentDueMode === 'DEPOSIT' ||
                            paymentDueMode === 'NOTHING'
                        "
                        class="flex justify-between border-t pt-3"
                    >
                        <span
                            class="text-slate-700 bg-yellow-300 p-1 font-medium"
                            >{{ $t('checkout.depositDueToday') }}</span
                        >
                        <span class="text-slate-700 text-lg font-semibold">{{
                            formatCurrency(depositAmount)
                        }}</span>
                    </li>

                    <li v-if="surchargeNote !== ''">
                        <content-reset :body="surchargeNote" />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PreviewSessionPricingFragment = graphql(`
    fragment PreviewSessionPricingFragment on Cart {
        items {
            __typename
            ... on CartProductItem {
                ...PreviewContractPricingCartProductItemFragment
            }
        }
        subtotal
        inclusiveTaxTotal
        exclusiveTaxTotal
        total
        surchargeNote(language: ENGLISH)
    }
`)

export const PreviewContractPricingCartProductItemFragment = graphql(`
    fragment PreviewContractPricingCartProductItemFragment on CartProductItem {
        unitPrice
        originalUnitPrice
        quantity
        description
        extendedTotal
        note
        productVariant {
            id
        }
    }
`)
</script>

<script setup lang="ts">
import { formatCurrency } from '~/composables/support/localization'
import ProgressBar from 'primevue/progressbar'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { computed } from 'vue'
import { Decimal } from '~/composables/support/decimal'
import type {
    PaymentDueMode,
    PreviewContractPricingCartProductItemFragmentFragment
} from '~/resources/graphql/graphql'
import AppMessage from '~/components/AppMessage.vue'
import ContentReset from '~/components/ContentReset.vue'

const props = defineProps<{
    cart: FragmentType<typeof PreviewSessionPricingFragment> | undefined | null
    paymentDueMode: PaymentDueMode | undefined | null
    depositAmount: string | undefined | null
    isLoading: boolean
}>()

const cartData = computed(() => {
    if (props.cart === undefined) {
        return undefined
    }

    if (props.cart === null) {
        return null
    }

    return getFragmentData(PreviewSessionPricingFragment, props.cart)
})

const formattedCartItems = computed(() => {
    if (cartData.value === undefined || cartData.value === null) {
        return []
    }

    const items: PreviewContractPricingCartProductItemFragmentFragment[] = []
    // For each item we have from the server
    cartData.value.items.forEach(item => {
        if (item.__typename !== 'CartProductItem') {
            throw new Error()
        }
        const itemData = getFragmentData(
            PreviewContractPricingCartProductItemFragment,
            item
        )

        // Find the index of any existing item with the same product variant, unit price, and original unit price
        const existingItemIndex = items.findIndex(
            existingItem =>
                existingItem.productVariant.id === itemData.productVariant.id &&
                existingItem.unitPrice === itemData.unitPrice &&
                existingItem.originalUnitPrice === itemData.originalUnitPrice
        )
        // If such an item exists, increment its quantity and extended total
        if (existingItemIndex > -1) {
            items[existingItemIndex] = {
                ...items[existingItemIndex],
                quantity: items[existingItemIndex].quantity + itemData.quantity,
                extendedTotal: new Decimal(
                    items[existingItemIndex].extendedTotal
                )
                    .plus(new Decimal(itemData.extendedTotal))
                    .toFixed(4)
            }
        }
        // Else add as new item
        else {
            items.push(itemData)
        }
    })

    return items
})

const inclusiveSalesTax = computed(() => {
    if (cartData.value === null || cartData.value === undefined) {
        return Decimal.zero()
    }

    return new Decimal(cartData.value.inclusiveTaxTotal)
})

const surchargeNote = computed(() => {
    if (cartData.value === null || cartData.value === undefined) {
        return ''
    }

    return cartData.value.surchargeNote
})

function isNotEqual(a: string, b: string) {
    return new Decimal(a).isNotEqualTo(new Decimal(b))
}
</script>
